import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface FaqDocument {
  fileName: string;
  fileExtension: string;
  fileData: string;
}

interface CachedFaqDocument {
  data: FaqDocument;
  cachedAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class FaqDocumentService {
  private readonly FAQ_DOCUMENTS_URL = `${environment.apiBaseUrl}/faqdocuments`;
  private readonly API_KEY = environment.apiKey;
  private readonly CACHE_KEY = 'faq_document_cache_v1';
  private readonly CACHE_TTL_MS = 12 * 60 * 60 * 1000;

  private cachedDocument: FaqDocument | null = null;
  private cachedAt: number = 0;
  private inFlightRequest$: Observable<FaqDocument | null> | null = null;

  constructor(private http: HttpClient) {
    this.restoreFromStorage();
  }

  getCachedDocument(): FaqDocument | null {
    if (this.isCacheValid()) {
      return this.cachedDocument;
    }
    this.clearCache();
    return null;
  }

  getDocument(forceRefresh = false): Observable<FaqDocument | null> {
    if (!forceRefresh) {
      const cached = this.getCachedDocument();
      if (cached) {
        return of(cached);
      }
    }

    if (this.inFlightRequest$) {
      return this.inFlightRequest$;
    }

    const headers = new HttpHeaders({
      'x-functions-key': this.API_KEY
    });

    this.inFlightRequest$ = this.http.get<any>(this.FAQ_DOCUMENTS_URL, { headers }).pipe(
      map((response) => {
        const file = response?.file;
        if (!file?.fileData || !file?.fileName || !file?.fileExtension) {
          return null;
        }

        const document: FaqDocument = {
          fileName: String(file.fileName),
          fileExtension: String(file.fileExtension),
          fileData: String(file.fileData)
        };
        return document;
      }),
      tap((document) => {
        if (document) {
          this.setCache(document);
        } else if (forceRefresh) {
          this.clearCache();
        }
      }),
      catchError((error) => {
        console.error('[FAQ] failed to fetch faq document', error);
        return of(null);
      }),
      finalize(() => {
        this.inFlightRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.inFlightRequest$;
  }

  preloadInBackground(forceRefresh = false): void {
    if (!forceRefresh && this.getCachedDocument()) {
      return;
    }

    this.getDocument(forceRefresh).pipe(take(1)).subscribe({
      next: () => {},
      error: () => {}
    });
  }

  private isCacheValid(): boolean {
    return !!this.cachedDocument && Date.now() - this.cachedAt < this.CACHE_TTL_MS;
  }

  private setCache(document: FaqDocument): void {
    this.cachedDocument = document;
    this.cachedAt = Date.now();

    const payload: CachedFaqDocument = {
      data: document,
      cachedAt: this.cachedAt
    };

    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(payload));
    } catch (error) {
      console.warn('[FAQ] unable to persist document in localStorage, using in-memory cache only', error);
    }
  }

  private restoreFromStorage(): void {
    try {
      const raw = localStorage.getItem(this.CACHE_KEY);
      if (!raw) {
        return;
      }

      const payload = JSON.parse(raw) as CachedFaqDocument;
      if (!payload?.data?.fileData || !payload.cachedAt) {
        this.clearCache();
        return;
      }

      this.cachedDocument = payload.data;
      this.cachedAt = payload.cachedAt;

      if (!this.isCacheValid()) {
        this.clearCache();
        return;
      }
    } catch {
      this.clearCache();
    }
  }

  private clearCache(): void {
    this.cachedDocument = null;
    this.cachedAt = 0;
    try {
      localStorage.removeItem(this.CACHE_KEY);
    } catch {
      // ignore storage cleanup errors
    }
  }
}